import { useState, useEffect, useRef, useMemo } from "react";
import {
  AlertTriangle, Zap, BarChart2, Clock, Users, Activity,
  Target, Shield, CheckCircle, List, Settings, RefreshCw,
  X, Download, Layers,
} from "lucide-react";
import Sidebar, { SIDEBAR_WIDTH } from "../components/layout/Sidebar";
import { TYPE, FONT_FAMILY } from "../constants/theme";
import Description from "../components/typography/Description";
import SectionNav from "../components/overview/SectionNav";
import { useTour } from "../tour/TourContext";

// Anchor id for a section name (used by the in-page navigator + ReportContent).
const wsSlug = (name) =>
  "ws-" + String(name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const JIRA = "https://ecosense-enviro.atlassian.net/browse";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const G = {
  white: "#FFFFFF",
  gray50: "#F9FAFB", gray100: "#F3F4F6", gray200: "#E5E7EB",
  gray300: "#D1D5DB", gray400: "#9CA3AF", gray500: "#6B7280",
  gray600: "#4B5563", gray700: "#374151", gray800: "#1F2937", gray900: "#111827",
  green50: "#F0FDF4", green500: "#22C55E", green600: "#16A34A", green700: "#15803D",
  amber50: "#FFFBEB", amber600: "#D97706", amber700: "#B45309",
  red50: "#FFF1F2", red600: "#DC2626", red700: "#B91C1C",
  blue50: "#EFF6FF", blue500: "#3B82F6", blue600: "#2563EB", blue700: "#1D4ED8",
  indigo50: "#EEF2FF", indigo100: "#E0E7FF", indigo600: "#4F46E5", indigo700: "#4338CA",
  border: "#E5E7EB",
};

// ─── SECTION CONSTANTS ────────────────────────────────────────────────────────
const SECTION_KEYS = {
  "Workload Summary":            "workloadSummary",
  "Ticket Inventory":            "ticketInventory",
  "Workload Distribution":       "workloadDistribution",
  "Risk Center":                 "riskAnalysis",
  "Ageing Analysis":             "ageingAnalysis",
  "Capacity & Delivery Outlook": "capacityAnalysis",
  "Team Comparison":             "teamComparison",
  "Ownership Hygiene":           "ownershipHygiene",
  "Recently Closed":             "recentlyClosed",
  "Commitment Analytics":        "commitmentAnalytics",
  "Action Recommendations":      "actionRecommendations",
  "Period Comparison":           "previousPeriod",
  "Focus Tickets":               "actionRecommendations",
};

const SECTION_ICONS = {
  "Workload Summary":            BarChart2,
  "Risk Center":                 AlertTriangle,
  "Action Recommendations":      Zap,
  "Capacity & Delivery Outlook": Activity,
  "Commitment Analytics":        Activity,
  "Ageing Analysis":             Clock,
  "Workload Distribution":       Layers,
  "Team Comparison":             Users,
  "Ownership Hygiene":           Shield,
  "Recently Closed":             CheckCircle,
  "Ticket Inventory":            List,
  "Period Comparison":           RefreshCw,
  "Focus Tickets":               Target,
};

const DEV_ONLY_SECTIONS = new Set([
  "Capacity & Delivery Outlook", "Commitment Analytics",
]);

const TIER4_ORDER = [
  "Commitment Analytics",
  "Ageing Analysis",
  "Workload Distribution",
  "Team Comparison",
  "Ownership Hygiene",
  "Recently Closed",
];

const PRESETS = {
  "Developer View": {
    sections: new Set([
      "Workload Summary", "Period Comparison", "Focus Tickets",
      "Risk Center", "Action Recommendations",
      "Capacity & Delivery Outlook", "Commitment Analytics", "Ageing Analysis",
    ]),
    ticketFilter: "open",
    forceAll: false,
  },
  "Team Lead": {
    sections: new Set([
      "Workload Summary", "Period Comparison",
      "Risk Center", "Action Recommendations",
      "Team Comparison", "Workload Distribution", "Ownership Hygiene", "Recently Closed",
    ]),
    ticketFilter: "all",
    forceAll: true,
  },
  "Executive": {
    sections: new Set([
      "Workload Summary", "Period Comparison",
      "Risk Center", "Action Recommendations",
      "Workload Distribution", "Recently Closed",
    ]),
    ticketFilter: "all",
    forceAll: true,
  },
};

const SECTION_GROUPS = [
  {
    label: "Core Intelligence",
    sections: ["Workload Summary", "Period Comparison", "Focus Tickets", "Risk Center", "Action Recommendations"],
  },
  {
    label: "Developer Analysis",
    note: "Requires a specific developer",
    sections: ["Capacity & Delivery Outlook", "Commitment Analytics", "Ageing Analysis"],
  },
  {
    label: "Team Analysis",
    sections: ["Workload Distribution", "Team Comparison", "Ownership Hygiene", "Recently Closed"],
  },
  {
    label: "Data Tables",
    note: "Best suited for export",
    sections: ["Ticket Inventory"],
  },
];

const DEFAULT_PRESET = "Developer View";

// ─── UTILITIES ────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  } catch { return "—"; }
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

function ageColor(days) {
  if (days != null && days > 21) return { bg: "#FEE2E2", c: "#991B1B" };
  return { bg: "#F1F5F9", c: "#64748B" };
}

function priorityCol(priority) {
  const p = (priority || "").toLowerCase();
  if (p === "critical" || p === "highest") return { bg: "#FEE2E2", c: "#991B1B" };
  if (p === "high") return { bg: "#DBEAFE", c: "#1D4ED8" };
  return { bg: "#F1F5F9", c: "#64748B" };
}

function statusCol() {
  return { bg: "#F1F5F9", c: "#475569" };
}

function csvEscape(v) {
  return `"${(v == null ? "" : String(v)).replace(/"/g, '""')}"`;
}

function buildQuery(cfg) {
  const p = new URLSearchParams({
    developer: cfg.developer,
    dateRange: cfg.dateRange,
    ticketFilter: cfg.ticketFilter,
  });
  if (cfg.dateRange === "sprint" && cfg.sprint) p.set("sprint", cfg.sprint);
  if (cfg.dateRange === "custom") {
    if (cfg.customStart) p.set("startDate", cfg.customStart);
    if (cfg.customEnd) p.set("endDate", cfg.customEnd);
  }
  return p.toString();
}

function groupStatusesForFunnel(byStatus) {
  const stages = [
    { label: "Backlog",     color: "#94A3B8", keys: ["backlog","to do","todo","pending","open","new","assigned","not started"] },
    { label: "In Progress", color: "#2563EB", keys: ["progress","development","doing","active","working"] },
    { label: "Review",      color: "#3B82F6", keys: ["review","pr","code review","in review"] },
    { label: "Testing",     color: "#64748B", keys: ["test","qa","quality","uat"] },
    { label: "Done",        color: "#475569", keys: ["done","closed","resolved","won't do","wont do","complete","finished"] },
  ];
  const counts = stages.map(() => 0);
  let ungrouped = 0;
  for (const item of (byStatus || [])) {
    const s = item.name.toLowerCase();
    const idx = stages.findIndex(st => st.keys.some(k => s.includes(k)));
    if (idx >= 0) counts[idx] += item.count;
    else ungrouped += item.count;
  }
  if (ungrouped > 0) counts[0] += ungrouped;
  return stages.map((st, i) => ({ ...st, count: counts[i] })).filter(st => st.count > 0);
}

// ─── SHARED PRIMITIVES ────────────────────────────────────────────────────────
function PriorityBadge({ priority }) {
  const col = priorityCol(priority);
  return (
    <span style={{ background: col.bg, color: col.c, fontSize: 11, fontWeight: 600, letterSpacing: "0.02em", padding: "2px 7px", borderRadius: 0, whiteSpace: "nowrap" }}>
      {priority || "—"}
    </span>
  );
}

function StatusBadge({ status }) {
  const col = statusCol(status);
  return (
    <span style={{ background: col.bg, color: col.c, fontSize: 11, fontWeight: 500, padding: "2px 7px", borderRadius: 0, whiteSpace: "nowrap" }}>
      {status || "—"}
    </span>
  );
}

function AgeBadge({ days }) {
  const col = ageColor(days);
  return (
    <span style={{ background: col.bg, color: col.c, fontSize: 11, fontWeight: 500, padding: "2px 7px", borderRadius: 0 }}>
      {days != null ? `${days}d` : "—"}
    </span>
  );
}

function HBar({ value, max, color, dark }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ height: 5, background: dark ? "rgba(255,255,255,0.1)" : "#EEF4FF", borderRadius: 0, overflow: "hidden" }}>
      <div style={{ height: 5, width: `${pct}%`, background: color || "#2563EB", borderRadius: 0 }} />
    </div>
  );
}

function TicketLink({ ticketNo, dark }) {
  if (!ticketNo) return <span style={{ color: dark ? "rgba(255,255,255,0.25)" : "#94A3B8" }}>—</span>;
  return (
    <a href={`${JIRA}/${ticketNo}`} target="_blank" rel="noreferrer"
      style={{ color: dark ? "#93C5FD" : "#2563EB", fontWeight: 600, fontSize: 12, textDecoration: "none", whiteSpace: "nowrap", fontFamily: TYPE.mono.fontFamily }}>
      {ticketNo}
    </a>
  );
}

function MetricCard({ label, value, sub, accent, dark }) {
  return (
    <div style={{
      background: dark ? "rgba(255,255,255,0.05)" : "#FFFFFF",
      border: dark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #BFDBFE",
      borderRadius: 0,
      padding: "16px 18px", flex: 1, minWidth: 110,
      borderLeft: `3px solid ${accent || "#2563EB"}`,
    }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: dark ? "#FFFFFF" : "#071A2F", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: dark ? "rgba(255,255,255,0.4)" : "#64748B", marginTop: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: dark ? "rgba(255,255,255,0.25)" : "#94A3B8", marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// Section-level "View Score Breakdown" disclosure — explains factors/weights
// behind a score or ranking. Per-ticket breakdowns (already sent by the
// backend, e.g. Focus Tickets' "Why") are left exactly as implemented.
function ScoreBreakdown({ title, intro, factors = [], footnote, dark, style = {} }) {
  const [open, setOpen] = useState(false);
  const fg = dark ? "rgba(255,255,255,0.55)" : "#64748B";
  const fgHover = dark ? "#FFFFFF" : "#0F172A";
  const border = dark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #BFDBFE";
  const panelBg = dark ? "rgba(255,255,255,0.05)" : "#F8FAFC";
  const panelBorder = dark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #E5E7EB";
  const headColor = dark ? "rgba(255,255,255,0.85)" : "#1F2937";
  const bodyColor = dark ? "rgba(255,255,255,0.55)" : "#475569";
  const mutedColor = dark ? "rgba(255,255,255,0.3)" : "#94A3B8";
  return (
    <div style={style}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ background: "transparent", border, borderRadius: 0, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: fg, cursor: "pointer", letterSpacing: "0.02em", whiteSpace: "nowrap", fontFamily: "inherit" }}
        onMouseEnter={e => { e.currentTarget.style.color = fgHover; }}
        onMouseLeave={e => { e.currentTarget.style.color = fg; }}
      >
        {open ? "Hide Score Breakdown ▲" : "View Score Breakdown ▾"}
      </button>
      {open && (
        <div style={{ marginTop: 10, padding: "14px 16px", background: panelBg, border: panelBorder, maxWidth: 480 }}>
          {title && <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: mutedColor, marginBottom: 8 }}>{title}</div>}
          {intro && <div style={{ fontSize: 12, color: bodyColor, lineHeight: 1.55, marginBottom: 10 }}>{intro}</div>}
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {factors.map((f, i) => (
              <li key={i} style={{ fontSize: 12, display: "flex", justifyContent: "space-between", gap: 12 }}>
                <span style={{ color: bodyColor }}>{f.label}</span>
                <span style={{ fontWeight: 700, color: headColor, fontFamily: TYPE.mono.fontFamily, fontSize: 12, flexShrink: 0 }}>{f.points}</span>
              </li>
            ))}
          </ul>
          {footnote && (
            <div style={{ fontSize: 11, color: mutedColor, marginTop: 10, lineHeight: 1.5, borderTop: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #E5E7EB", paddingTop: 10 }}>
              {footnote}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TableHead({ cols }) {
  return (
    <thead>
      <tr style={{ background: "#F8FAFC" }}>
        {cols.map(c => (
          <th key={c} style={{ padding: "8px 10px", fontSize: 11, fontWeight: 600, color: "#64748B", textAlign: "left", borderBottom: "1px solid #BFDBFE", textTransform: "uppercase", letterSpacing: "0.08em" }}>{c}</th>
        ))}
      </tr>
    </thead>
  );
}

function AIInsightHook({ insight, dark }) {
  if (!insight) return null;
  return (
    <div style={{
      background: dark ? "rgba(37,99,235,0.15)" : "#EFF6FF",
      border: dark ? "1px solid rgba(59,130,246,0.3)" : "1px solid #BFDBFE",
      borderRadius: 0, padding: "10px 14px", marginTop: 12, fontSize: 12,
      color: dark ? "#93C5FD" : "#1D4ED8", display: "flex", gap: 8,
    }}>
      <span style={{ fontWeight: 700, letterSpacing: "0.05em" }}>AI</span>
      <span>{insight}</span>
    </div>
  );
}

function NoDevNote() {
  return (
    <p style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", margin: "12px 0 0" }}>
      Select a specific developer in the command bar to enable this section.
    </p>
  );
}

function NoData() {
  return <p style={{ fontSize: 12, color: "#94A3B8", margin: "12px 0 0" }}>No data for the selected filters.</p>;
}

// ─── SVG CHARTS ──────────────────────────────────────────────────────────────

function TrendChart({ data }) {
  if (!data || data.length < 2) return null;
  const W = 500, H = 90, pT = 14, pB = 22, pL = 4, pR = 4;
  const iW = W - pL - pR, iH = H - pT - pB;
  const hasCarry = data.some(d => (d.carryForward || 0) > 0);
  const maxVal = Math.max(...data.map(d => Math.max(d.committed || 0, d.completed || 0, d.carryForward || 0)), 1);
  const px = i => pL + (i / (data.length - 1)) * iW;
  const py = v => pT + (1 - v / maxVal) * iH;
  const pts = key => data.map((d, i) => `${px(i).toFixed(1)},${py(d[key] || 0).toFixed(1)}`).join(" ");
  const labelAt = new Set([0, Math.floor((data.length - 1) / 2), data.length - 1]);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H }}>
      {[0, 0.5, 1].map(f => (
        <line key={f} x1={pL} y1={py(maxVal * f)} x2={W - pR} y2={py(maxVal * f)} stroke={"#EEF4FF"} strokeWidth="1" />
      ))}
      <polyline points={pts("committed")} fill="none" stroke={"#3B82F6"} strokeWidth="2" strokeLinejoin="round" />
      <polyline points={pts("completed")} fill="none" stroke={"#64748B"} strokeWidth="2" strokeLinejoin="round" />
      {hasCarry && (
        <polyline points={pts("carryForward")} fill="none" stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="4,3" strokeLinejoin="round" />
      )}
      {data.map((d, i) => labelAt.has(i) ? (
        <text key={i} x={px(i)} y={H - 4} textAnchor="middle" fontSize="11" fill={"#94A3B8"}>{d.date?.slice(5)}</text>
      ) : null)}
      <rect x={pL} y={2} width={8} height={5} rx="0" fill={"#3B82F6"} />
      <text x={pL + 11} y={8} fontSize="10" fill={"#64748B"}>Committed</text>
      <rect x={pL + 82} y={2} width={8} height={5} rx="0" fill={"#64748B"} />
      <text x={pL + 93} y={8} fontSize="10" fill={"#64748B"}>Completed</text>
      {hasCarry && (
        <>
          <line x1={pL + 162} y1={5} x2={pL + 172} y2={5} stroke="#94A3B8" strokeWidth="1.5" strokeDasharray="3,2" />
          <text x={pL + 176} y={8} fontSize="10" fill="#94A3B8">Carry-forward</text>
        </>
      )}
    </svg>
  );
}

function DeliveryProjectionChart({ data }) {
  if (!data || data.noData) return null;
  const { openTickets, estimatedDaysHistorical, estimatedDaysRecent } = data;
  if (!estimatedDaysHistorical && !estimatedDaysRecent) return null;
  if (!openTickets || openTickets === 0) return null;
  const maxDays = Math.ceil(Math.max(estimatedDaysHistorical || 0, estimatedDaysRecent || 0, 1) * 1.15);
  const W = 480, H = 130, pT = 20, pB = 30, pL = 42, pR = 16;
  const iW = W - pL - pR, iH = H - pT - pB;
  const px = d => pL + (d / maxDays) * iW;
  const py = v => pT + (1 - Math.max(0, v) / openTickets) * iH;
  const histPts = estimatedDaysHistorical
    ? `${px(0).toFixed(1)},${py(openTickets).toFixed(1)} ${px(estimatedDaysHistorical).toFixed(1)},${py(0).toFixed(1)}`
    : null;
  const recentPts = estimatedDaysRecent
    ? `${px(0).toFixed(1)},${py(openTickets).toFixed(1)} ${px(estimatedDaysRecent).toFixed(1)},${py(0).toFixed(1)}`
    : null;
  const yTicks = [0, Math.round(openTickets / 2), openTickets];
  const xTicks = [0, Math.round(maxDays * 0.5), maxDays];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H }}>
      {yTicks.map(v => (
        <line key={v} x1={pL} y1={py(v)} x2={W - pR} y2={py(v)} stroke={"#EEF4FF"} strokeWidth="1" />
      ))}
      {yTicks.map(v => (
        <text key={v} x={pL - 5} y={py(v) + 4} textAnchor="end" fontSize="11" fill={"#94A3B8"}>{v}</text>
      ))}
      {histPts && (
        <polyline points={histPts} fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="5,3" strokeLinejoin="round" />
      )}
      {recentPts && (
        <polyline points={recentPts} fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinejoin="round" />
      )}
      <line x1={px(0)} y1={pT} x2={px(0)} y2={H - pB} stroke="#E2E8F0" strokeWidth="1" strokeDasharray="3,2" />
      <text x={px(0) + 5} y={pT + 11} fontSize="10" fill="#94A3B8">Today</text>
      {xTicks.map(d => (
        <text key={d} x={px(d)} y={H - pB + 14} textAnchor="middle" fontSize="11" fill="#94A3B8">{d === 0 ? "Now" : `${d}d`}</text>
      ))}
      {estimatedDaysRecent && (
        <circle cx={px(estimatedDaysRecent)} cy={py(0)} r="4" fill="#2563EB" />
      )}
      <line x1={pL + 4} y1={6} x2={pL + 22} y2={6} stroke="#2563EB" strokeWidth="2.5" />
      <text x={pL + 26} y={9} fontSize="10" fill="#64748B">Recent pace</text>
      <line x1={pL + 96} y1={6} x2={pL + 114} y2={6} stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="4,3" />
      <text x={pL + 118} y={9} fontSize="10" fill="#94A3B8">Historical pace</text>
    </svg>
  );
}

function AgeingBarChart({ distribution }) {
  if (!distribution || distribution.length === 0) return null;
  const maxCount = Math.max(...distribution.map(d => d.count), 1);
  const W = 340, H = 110, pT = 18, pB = 36, pL = 8, pR = 8;
  const iW = W - pL - pR, iH = H - pT - pB;
  const barW = Math.max(24, Math.floor(iW / distribution.length) - 8);
  const bucketColors = { "0-7d": "#64748B", "8-14d": "#3B82F6", "15-30d": "#2563EB", "31-60d": "#DC2626", "60+d": "#991B1B" };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H }}>
      {[0, 0.5, 1].map(f => (
        <line key={f} x1={pL} y1={pT + (1 - f) * iH} x2={W - pR} y2={pT + (1 - f) * iH} stroke={"#EEF4FF"} strokeWidth="1" />
      ))}
      {distribution.map((d, i) => {
        const barH = maxCount > 0 ? (d.count / maxCount) * iH : 0;
        const x = pL + (i / distribution.length) * iW + (iW / distribution.length - barW) / 2;
        const y = pT + iH - barH;
        const color = bucketColors[d.range] || "#3B82F6";
        return (
          <g key={d.range}>
            <rect x={x} y={y} width={barW} height={Math.max(barH, 1)} rx="0" fill={color} opacity="0.85" />
            {d.count > 0 && (
              <text x={x + barW / 2} y={Math.max(y - 4, pT + 10)} textAnchor="middle" fontSize="10" fontWeight="700" fill={color}>{d.count}</text>
            )}
            <text x={x + barW / 2} y={H - pB + 16} textAnchor="middle" fontSize="11" fill={"#64748B"}>{d.range}</text>
          </g>
        );
      })}
    </svg>
  );
}

function TeamGroupedBarChart({ developers }) {
  if (!developers || developers.length === 0) return null;
  const topDevs = developers.slice(0, 8);
  const maxOpen = Math.max(...topDevs.map(d => d.open), 1);
  const ROW_H = 30, PAD_T = 8, LEFT = 124, RIGHT = 20, W = 480;
  const H = PAD_T + topDevs.length * ROW_H + 12;
  const barMaxW = W - LEFT - RIGHT;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H }}>
      {topDevs.map((d, i) => {
        const y = PAD_T + i * ROW_H;
        const openW = Math.max(4, (d.open / maxOpen) * barMaxW);
        const critW = d.criticalOpen > 0 ? Math.max(4, (d.criticalOpen / maxOpen) * barMaxW) : 0;
        const name = d.developer.length > 18 ? d.developer.slice(0, 17) + "…" : d.developer;
        return (
          <g key={d.developer}>
            <text x={LEFT - 8} y={y + 11} textAnchor="end" fontSize="11" fill={"#334155"}>{name}</text>
            <rect x={LEFT} y={y + 2} width={openW} height={11} rx="0" fill={"#3B82F6"} opacity="0.75" />
            {critW > 0 && (
              <rect x={LEFT} y={y + 16} width={critW} height={7} rx="0" fill={"#DC2626"} opacity="0.8" />
            )}
            <text x={LEFT + openW + 5} y={y + 11} fontSize="10" fontWeight="700" fill={"#2563EB"}>{d.open}</text>
            {critW > 0 && (
              <text x={LEFT + critW + 5} y={y + 22} fontSize="11" fill={"#DC2626"}>{d.criticalOpen} crit</text>
            )}
          </g>
        );
      })}
      <text x={LEFT} y={H - 2} fontSize="10" fill="#94A3B8">Open tickets (blue) · Critical open (red)</text>
    </svg>
  );
}

function CommitmentHeatmap({ dailyTrend }) {
  if (!dailyTrend || dailyTrend.length < 7) return null;
  const TILE = 34, GAP = 5;
  const perRow = Math.min(14, dailyTrend.length);
  const rows = Math.ceil(dailyTrend.length / perRow);
  const W = perRow * (TILE + GAP) - GAP;
  const H = rows * (TILE + GAP) - GAP + 18;
  const tileColor = d => {
    if (d.committed === 0) return "rgba(255,255,255,0.06)";
    if (d.completed >= d.committed) return "#2563EB";
    if (d.completed > 0) return "#475569";
    return "#DC2626";
  };
  const textColor = d => {
    if (d.committed === 0) return "#94A3B8";
    return "#FFFFFF";
  };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W + 40, height: H }}>
      {dailyTrend.map((d, i) => {
        const row = Math.floor(i / perRow);
        const col = i % perRow;
        const x = col * (TILE + GAP);
        const y = row * (TILE + GAP);
        const color = tileColor(d);
        const tc = textColor(d);
        return (
          <g key={i}>
            <rect x={x} y={y} width={TILE} height={TILE} rx="0" fill={color} />
            <text x={x + TILE / 2} y={y + TILE / 2 + 3} textAnchor="middle" fontSize="11" fontWeight="700" fill={tc}>
              {d.committed > 0 ? `${d.completed}/${d.committed}` : "—"}
            </text>
          </g>
        );
      })}
      {dailyTrend.slice(0, perRow).map((d, i) => {
        if (i % 3 !== 0) return null;
        const x = i * (TILE + GAP) + TILE / 2;
        return (
          <text key={i} x={x} y={H - 2} textAnchor="middle" fontSize="10" fill={"#94A3B8"}>{d.date?.slice(5)}</text>
        );
      })}
    </svg>
  );
}

function RiskHeatmapGrid({ riskAnalysis, ownershipHygiene }) {
  const cells = [
    { label: "Critical Risk",  count: riskAnalysis.criticalCount || 0,        color: "#FCA5A5", bg: "rgba(220,38,38,0.15)",   border: "#DC2626", action: "Prioritize before all other work" },
    { label: "Ageing Risk",    count: riskAnalysis.ageingCount || 0,          color: "rgba(255,255,255,0.6)", bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.15)", action: "Review and close or reprioritize" },
    { label: "Idle Risk",      count: riskAnalysis.idleCount || 0,            color: "rgba(255,255,255,0.6)", bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.15)", action: "Update status or escalate today" },
    { label: "Ownership Risk", count: ownershipHygiene?.unassignedCount || 0, color: "rgba(255,255,255,0.5)", bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.1)",  action: "Assign owners to prevent drift" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
      {cells.map(cell => (
        <div key={cell.label} style={{
          background: cell.bg,
          borderRadius: 0, padding: "14px 16px",
          borderLeft: `3px solid ${cell.border}`,
          opacity: cell.count === 0 ? 0.5 : 1,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: cell.color, textTransform: "uppercase", letterSpacing: "0.07em" }}>{cell.label}</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: cell.color, lineHeight: 1.15, marginTop: 4 }}>{cell.count}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 5 }}>{cell.action}</div>
        </div>
      ))}
    </div>
  );
}

function PipelineFunnelChart({ byStatus }) {
  const stages = groupStatusesForFunnel(byStatus);
  if (stages.length === 0) return null;
  const maxCount = Math.max(...stages.map(s => s.count), 1);
  const BAR_H = 22, GAP = 7, LEFT = 88, RIGHT = 56, W = 460;
  const H = stages.length * (BAR_H + GAP) + 4;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H }}>
      {stages.map((s, i) => {
        const y = i * (BAR_H + GAP);
        const barW = Math.max(6, (s.count / maxCount) * (W - LEFT - RIGHT));
        return (
          <g key={s.label}>
            <text x={LEFT - 7} y={y + BAR_H / 2 + 4} textAnchor="end" fontSize="11" fill={"#475569"}>{s.label}</text>
            <rect x={LEFT} y={y} width={barW} height={BAR_H} rx="0" fill={s.color} opacity="0.8" />
            <text x={LEFT + barW + 6} y={y + BAR_H / 2 + 4} fontSize="11" fontWeight="700" fill={s.color}>{s.count}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── DOWNLOAD HELPERS ─────────────────────────────────────────────────────────
function reportFilename(reportData, ext) {
  const m = reportData.meta || {};
  const stamp = (m.generatedAt ? new Date(m.generatedAt) : new Date())
    .toISOString().slice(0, 10);
  const slug = `${m.developer || "all"}-${m.period || "report"}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `workload-report-${slug}-${stamp}.${ext}`;
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadCSV(reportData) {
  const tickets = reportData.sections.ticketInventory?.tickets || [];
  const headers = ["Ticket No", "Summary", "Priority", "Status", "Assigned To", "Sprint", "Module", "Issue Type", "Age (days)", "Updated On", "Raised On"];
  const rows = tickets.map(t =>
    [t.ticketNo, t.summary, t.priority, t.status, t.assignedTo, t.sprint, t.module, t.issueType, t.ageingDays, t.updatedOn, t.raisedOn]
      .map(csvEscape).join(",")
  );
  // Prepend a UTF-8 BOM + use CRLF so Excel opens non-ASCII names correctly.
  const BOM = String.fromCharCode(0xFEFF);
  const body = BOM + [headers.map(csvEscape).join(","), ...rows].join("\r\n");
  triggerDownload(new Blob([body], { type: "text/csv;charset=utf-8;" }), reportFilename(reportData, "csv"));
}

function downloadExcel(reportData) {
  const tickets = reportData.sections.ticketInventory?.tickets || [];
  const esc = v => String(v == null ? "" : v)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const cols = ["Ticket No", "Summary", "Priority", "Status", "Assigned To", "Sprint", "Module", "Issue Type", "Age (days)", "Updated On"];
  const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="UTF-8"/></head><body><table>
<tr>${cols.map(c => `<th style="background:#F3F4F6;font-weight:bold">${esc(c)}</th>`).join("")}</tr>
${tickets.map(t => `<tr>${[t.ticketNo, t.summary, t.priority, t.status, t.assignedTo, t.sprint, t.module, t.issueType, t.ageingDays, t.updatedOn].map(v => `<td>${esc(v)}</td>`).join("")}</tr>`).join("")}
</table></body></html>`;
  triggerDownload(new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" }), reportFilename(reportData, "xls"));
}

// ─── SECTION WRAPPER ──────────────────────────────────────────────────────────
function ReportSection({ id, title, subtitle, desc, icon: Icon, children, dark, headerRight }) {
  return (
    <div id={id} style={{
      background: dark ? "#071A2F" : "#FFFFFF",
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #BFDBFE",
      borderRadius: 0, marginBottom: 16,
      boxShadow: "0 2px 8px rgba(7,26,47,0.07)",
    }} className="report-section">
      <div style={{
        padding: "12px 20px", background: dark ? "#040E1C" : "#071A2F",
        borderRadius: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "none",
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 0 }}>
          {Icon && <Icon size={14} color="rgba(255,255,255,0.35)" style={{ marginTop: 2, flexShrink: 0 }} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#FFFFFF" }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>{subtitle}</div>}
            {desc && <Description light style={{ marginTop: 4 }}>{desc}</Description>}
          </div>
        </div>
        {headerRight && <div style={{ flexShrink: 0 }}>{headerRight}</div>}
      </div>
      <div style={{ padding: "18px 20px" }}>{children}</div>
    </div>
  );
}

// ─── SECTION COMPONENTS ───────────────────────────────────────────────────────

function ExecutiveSummarySection({ reportData, developer }) {
  const s = reportData.sections;
  const ws = s.workloadSummary || {};
  const ca = s.capacityAnalysis;
  const df = s.deliveryForecast;
  const commit = s.commitmentAnalytics;
  const isDev = developer && developer !== "all";

  // Risk score
  const score = Math.min(100, (ws.critical || 0) * 15 + (ws.idleCount || 0) * 10 + (ws.ageingCount || 0) * 5);
  const riskLevel = score >= 60 ? "Critical" : score >= 25 ? "Warning" : "Healthy";
  const riskColor = score >= 60 ? "#DC2626" : "#2563EB";
  const riskBg   = "#F8FAFC";

  // Capacity status
  let capStatus = "N/A", capSub = "Select a developer", capColor = "#64748B", capBg = "#F8FAFC";
  if (isDev && ca && !ca.noData && ca.estimatedDaysToClose != null) {
    const d = ca.estimatedDaysToClose;
    if (d <= 10)      { capStatus = "Healthy";    capSub = `~${d}d to clear backlog`; capColor = "#2563EB"; }
    else if (d <= 21) { capStatus = "Busy";       capSub = `~${d}d to clear backlog`; capColor = "#475569"; }
    else              { capStatus = "Overloaded"; capSub = `~${d}d to clear backlog`; capColor = "#DC2626"; }
  }

  // Delivery outlook
  let delivStatus = "N/A", delivSub = "Select a developer", delivColor = "#64748B", delivBg = "#F8FAFC";
  if (isDev && df && !df.noData && df.estimatedDaysRecent != null) {
    const d = df.estimatedDaysRecent;
    const rel = (commit && !commit.noData) ? commit.commitmentReliability : 50;
    if (d <= 14 && rel >= 70)      { delivStatus = "On Track"; delivSub = `~${d}d at recent pace`; delivColor = "#2563EB"; }
    else if (d <= 28 || rel >= 55) { delivStatus = "At Risk";  delivSub = `~${d}d at recent pace`; delivColor = "#475569"; }
    else                           { delivStatus = "Behind";   delivSub = `~${d}d at recent pace`; delivColor = "#DC2626"; }
  }

  // Top action
  const items = s.actionRecommendations?.items || [];
  const topAction = items.find(i => i.severity === "high") || items[0];
  const actionText = topAction ? topAction.message : "No critical issues detected";

  const cells = [
    { label: "RISK LEVEL", value: riskLevel,   sub: `Score ${score}/100`,  color: riskColor  },
    { label: "CAPACITY",   value: capStatus,   sub: capSub,                 color: capColor   },
    { label: "DELIVERY",   value: delivStatus, sub: delivSub,               color: delivColor },
    { label: "TOP ACTION", value: null,        sub: actionText,             color: "#475569"  },
  ];

  return (
    <div id="ws-executive" style={{
      background: "#FFFFFF", border: "1px solid #BFDBFE", borderRadius: 0, marginBottom: 16,
      boxShadow: "0 2px 8px rgba(7,26,47,0.07)",
    }} className="report-section">
      <div style={{ padding: "12px 20px", background: "#071A2F", borderRadius: 0 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Executive Summary</div>
            <Description light style={{ marginTop: 4 }}>At-a-glance risk, capacity, and delivery status for the selected scope.</Description>
          </div>
          <ScoreBreakdown
            dark
            title="How Risk Level is calculated"
            intro="Score starts at 0 and is capped at 100."
            factors={[
              { label: "Critical tickets", points: "+15 each" },
              { label: "Idle tickets", points: "+10 each" },
              { label: "Ageing tickets", points: "+5 each" },
            ]}
            footnote="60+ Critical · 25–59 Warning · below 25 Healthy. Capacity uses days-to-clear-backlog (≤10 Healthy, ≤21 Busy, else Overloaded). Delivery combines recent-pace days with Commitment Reliability (≤14d & ≥70% On Track, ≤28d or ≥55% At Risk, else Behind)."
          />
        </div>
      </div>
      <div style={{ padding: "16px 20px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {cells.map((cell, i) => (
          <div key={i} style={{ background: "#F8FAFC", borderRadius: 0, padding: "12px 14px", borderLeft: `3px solid ${cell.color}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.07em" }}>{cell.label}</div>
            {cell.value && (
              <div style={{ fontSize: 17, fontWeight: 800, color: cell.color, marginTop: 4, lineHeight: 1.2 }}>{cell.value}</div>
            )}
            <div style={{ fontSize: i === 3 ? 11 : 10, color: i === 3 ? "#475569" : "#64748B", marginTop: cell.value ? 4 : 8, lineHeight: 1.4 }}>{cell.sub}</div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

function PeriodComparisonSection({ current, previous }) {
  if (!previous) {
    return (
      <p style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", lineHeight: 1.6 }}>
        Period comparison is available for Last 7, 14, and 30 Day ranges. Select one of those periods to see how your workload changed compared to the prior equivalent window.
      </p>
    );
  }

  function delta(cur, prev, lowerBetter) {
    const diff = cur - prev;
    const direction = lowerBetter
      ? (diff < 0 ? "improving" : diff > 0 ? "worsening" : "unchanged")
      : (diff > 0 ? "improving" : diff < 0 ? "worsening" : "unchanged");
    const pct = prev > 0 ? Math.round(Math.abs(diff) / prev * 100) : null;
    return { diff, direction, pct };
  }

  const rows = [
    { label: "Open Tickets",   cur: current.open,        prev: previous.open,        lowerBetter: true },
    { label: "Closed Tickets", cur: current.closed,      prev: previous.closed,      lowerBetter: false },
    { label: "Critical Open",  cur: current.critical,    prev: previous.critical,    lowerBetter: true },
    { label: "Avg Age (open)", cur: current.avgAge,      prev: previous.avgAge,      lowerBetter: true,  suffix: "d" },
    { label: "Ageing 14+ d",  cur: current.ageingCount, prev: previous.ageingCount, lowerBetter: true },
  ];

  const dirColor = { improving: "#2563EB", worsening: "#DC2626", unchanged: "#475569" };
  const dirBg    = { improving: "#EFF6FF", worsening: "#FEE2E2", unchanged: "#F1F5F9" };
  const dirLabel = { improving: "Improving", worsening: "Worsening", unchanged: "Unchanged" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <Description style={{ marginBottom: 4, color: "#64748B" }}>
        Each metric compared against the same-length period immediately before this one. "Improving" means the change moved in the good direction for that metric.
      </Description>
      {rows.map(row => {
        const d = delta(row.cur, row.prev, row.lowerBetter);
        const sfx = row.suffix || "";
        const diffStr = d.diff > 0 ? `+${d.diff}${sfx}` : `${d.diff}${sfx}`;
        const pctStr = d.pct != null ? ` (${d.pct}%)` : "";
        return (
          <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: "1px solid #EEF4FF" }}>
            <span style={{ fontSize: 12, color: "#475569", minWidth: 114 }}>{row.label}</span>
            <span style={{ fontSize: 12, color: "#94A3B8" }}>{row.prev}{sfx}</span>
            <span style={{ fontSize: 11, color: "#CBD5E1" }}>→</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#071A2F" }}>{row.cur}{sfx}</span>
            <div style={{ flex: 1 }} />
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 0, whiteSpace: "nowrap",
              background: dirBg[d.direction], color: dirColor[d.direction],
            }}>
              {d.direction === "unchanged" ? "Unchanged" : `${diffStr}${pctStr} — ${dirLabel[d.direction]}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function FocusTicketsSection({ data }) {
  const tickets = data?.focusTickets || [];
  if (tickets.length === 0) {
    return <p style={{ fontSize: 12, color: "#94A3B8" }}>No open tickets requiring immediate attention in this period.</p>;
  }
  const riskBorder = { Critical: "#DC2626", High: "#2563EB", Medium: "#3B82F6" };
  const riskBg     = { Critical: "#FEE2E2", High: "#FFFFFF", Medium: "#FFFFFF"  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {tickets.map((t, i) => (
        <div key={t.ticketNo || i} style={{
          background: "#FFFFFF",
          borderRadius: 0, padding: "10px 14px",
          border: "1px solid #BFDBFE",
          borderLeft: `3px solid ${riskBorder[t.riskLevel] || "#94A3B8"}`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700 }}>#{i + 1}</span>
            <TicketLink ticketNo={t.ticketNo} />
            <PriorityBadge priority={t.priority} />
            <StatusBadge status={t.status} />
            {t.ageingDays != null && <AgeBadge days={t.ageingDays} />}
          </div>
          <div style={{ fontSize: 12, color: "#334155", marginBottom: 3, fontWeight: 500, lineHeight: 1.4 }}>{t.summary || "—"}</div>
          <div style={{ fontSize: 11, color: "#64748B" }}>
            <span style={{ fontWeight: 600, color: "#475569" }}>Why: </span>{t.reason}
          </div>
        </div>
      ))}
    </div>
  );
}

function WorkloadSummarySection({ data }) {
  if (!data) return <NoData />;
  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <MetricCard label="Total Tickets"      value={data.total}        accent="#2563EB" />
        <MetricCard label="Open"               value={data.open}         accent="#2563EB" />
        <MetricCard label="Closed"             value={data.closed}       accent="#64748B" />
        <MetricCard label="Critical Open"      value={data.critical}     accent="#DC2626" />
        <MetricCard label="High Priority Open" value={data.high}         accent="#2563EB" />
        <MetricCard label="Avg Age (open)"     value={`${data.avgAge}d`} accent={data.avgAge > 14 ? "#DC2626" : "#64748B"} />
        <MetricCard label="Idle 5+ days"       value={data.idleCount}    accent="#64748B" />
        <MetricCard label="Ageing 14+ days"    value={data.ageingCount}  accent="#64748B" />
      </div>
      <AIInsightHook insight={data.aiInsight} />
    </>
  );
}

function DistBar({ name, count, max, color, dark }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: dark ? "rgba(255,255,255,0.5)" : "#475569" }}>{name}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: dark ? "#FFFFFF" : "#071A2F" }}>{count}</span>
      </div>
      <HBar value={count} max={max} color={color} dark={dark} />
    </div>
  );
}

function WorkloadDistributionSection({ data }) {
  if (!data) return <NoData />;
  const maxP = Math.max(...(data.byPriority || []).map(x => x.count), 1);
  const maxM = Math.max(...(data.byModule || []).map(x => x.count), 1);
  const pColors = { Critical: "#DC2626", Highest: "#DC2626", High: "#2563EB", Medium: "#64748B", Low: "#94A3B8", Lowest: "#94A3B8" };
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>By Priority</div>
          {(data.byPriority || []).map(x => <DistBar key={x.name} name={x.name} count={x.count} max={maxP} color={pColors[x.name] || "#3B82F6"} />)}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Pipeline Status</div>
          <Description style={{ marginBottom: 10, color: "#64748B" }}>Every status grouped into Backlog, In Progress, Review, Testing, or Done by keyword.</Description>
          <PipelineFunnelChart byStatus={data.byStatus} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>By Module</div>
          {(data.byModule || []).map(x => <DistBar key={x.name} name={x.name} count={x.count} max={maxM} color="#2563EB" />)}
        </div>
      </div>
      <AIInsightHook insight={data.aiInsight} />
    </>
  );
}

function RiskCenterSection({ riskData, ownershipData }) {
  if (!riskData) return <NoData />;
  const seen = new Set();
  const unified = [];
  const tag = (list, category) => {
    for (const t of (list || [])) {
      if (!seen.has(t.ticketNo)) { seen.add(t.ticketNo); unified.push({ ...t, category }); }
    }
  };
  tag(riskData.critical, "Critical");
  tag(riskData.ageing,   "Ageing");
  tag(riskData.idle,     "Idle");
  tag(ownershipData?.unassigned, "Unassigned");
  const catColor = { Critical: "#FCA5A5", Ageing: "rgba(255,255,255,0.4)", Idle: "rgba(255,255,255,0.4)", Unassigned: "rgba(255,255,255,0.3)" };
  return (
    <>
      <RiskHeatmapGrid riskAnalysis={riskData} ownershipHygiene={ownershipData} />
      <div style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Risk Tickets</div>
          <ScoreBreakdown
            dark
            title="How each risk bucket is defined"
            factors={[
              { label: "Critical", points: "Risk Score ≥ 60" },
              { label: "Ageing", points: "open 14+ days" },
              { label: "Idle", points: "no activity 5+ days" },
              { label: "Unassigned", points: "no assignee" },
            ]}
            footnote="A ticket can belong to more than one bucket at once. Critical uses the same Risk Score as the Critical Issues panel on the Dashboard."
          />
        </div>
        <Description light style={{ marginBottom: 10 }}>
          Every ticket flagged into at least one risk bucket above, merged into one list — start with anything tagged CRITICAL.
        </Description>
        {unified.length === 0 ? (
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>No risk tickets detected.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {unified.slice(0, 15).map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <TicketLink ticketNo={t.ticketNo} dark />
                <span style={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,0.55)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.summary}>{t.summary}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 0, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)", whiteSpace: "nowrap" }}>{t.priority || "—"}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 0, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap" }}>{t.ageingDays != null ? `${t.ageingDays}d` : "—"}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: catColor[t.category] || "rgba(255,255,255,0.4)", minWidth: 60, textAlign: "right" }}>{t.category.toUpperCase()}</span>
              </div>
            ))}
            {unified.length > 15 && (
              <Description light style={{ marginTop: 6 }}>+ {unified.length - 15} more risk tickets not shown here.</Description>
            )}
          </div>
        )}
      </div>
      <AIInsightHook insight={riskData.aiInsight} dark />
    </>
  );
}

function AgeingAnalysisSection({ data }) {
  if (!data) return <NoData />;
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Age Distribution (open tickets)</div>
          <Description style={{ marginBottom: 10, color: "#64748B" }}>How many open tickets fall into each age bucket, from under a week to over 60 days.</Description>
          <AgeingBarChart distribution={data.distribution} />
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Oldest 5 Open Tickets</div>
          <Description style={{ marginBottom: 10, color: "#64748B" }}>Sorted by age, oldest first.</Description>
          {(data.oldest5 || []).length === 0
            ? <p style={{ fontSize: 12, color: "#94A3B8" }}>No open tickets.</p>
            : (data.oldest5 || []).map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: "#94A3B8", width: 16 }}>{i + 1}.</span>
                <TicketLink ticketNo={t.ticketNo} />
                <span style={{ flex: 1, fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.summary}</span>
                <AgeBadge days={t.ageingDays} />
              </div>
            ))}
        </div>
      </div>
      <AIInsightHook insight={data.aiInsight} />
    </>
  );
}

function CapacityDeliverySection({ capacity, forecast, developer }) {
  if (!developer || developer === "all") return <NoDevNote />;
  if (!capacity) return <NoData />;
  if (capacity.noData) return <p style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic" }}>No commitment history found. Start logging daily commitments to enable this section.</p>;
  const rel = capacity.commitmentReliability || 0;
  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <MetricCard label="Open Tickets"           value={capacity.openTickets}          accent="#2563EB" />
        <MetricCard label="Avg Completions / day"  value={capacity.avgDailyCompletion}   accent="#64748B" />
        <MetricCard label="Est. Days to Clear"     value={capacity.estimatedDaysToClose != null ? `${capacity.estimatedDaysToClose}d` : "—"} accent="#64748B" sub="at historical pace" />
        <MetricCard label="Commitment Reliability" value={`${rel}%`} accent={rel >= 80 ? "#2563EB" : rel >= 60 ? "#64748B" : "#DC2626"} sub="days fully completed" />
      </div>
      {forecast && !forecast.noData && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Delivery Projection</div>
          <DeliveryProjectionChart data={forecast} />
          <p style={{ fontSize: 11, color: "#94A3B8", marginTop: 6, fontStyle: "italic" }}>
            Projection based on {forecast.dataPoints} days of data. Blue = recent 7-day pace. Gray = all-time pace.
          </p>
        </div>
      )}
    </>
  );
}

function CommitmentAnalyticsSection({ data, developer }) {
  if (!developer || developer === "all") return <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontStyle: "italic", margin: "12px 0 0" }}>Select a specific developer in the command bar to enable this section.</p>;
  if (!data) return <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", margin: "12px 0 0" }}>No data for the selected filters.</p>;
  if (data.noData) return <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>No commitment records found. Use the Daily Commitment tracker to build history.</p>;
  const blockers = Object.entries(data.blockerDistribution || {}).sort((a, b) => b[1] - a[1]);
  const maxB = Math.max(...blockers.map(b => b[1]), 1);
  const rel = data.commitmentReliability || 0;
  const lbl = { fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: "0.08em" };
  return (
    <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <MetricCard label="Commitment Reliability" value={`${rel}%`} accent={rel >= 80 ? "#2563EB" : rel >= 60 ? "rgba(255,255,255,0.2)" : "#DC2626"} sub="days fully completed" dark />
        <MetricCard label="Avg Daily Commitments"  value={data.averageDailyCommitments}  accent="rgba(255,255,255,0.2)" sub="tickets committed / day" dark />
        <MetricCard label="Avg Daily Completions"  value={data.averageDailyCompletion}    accent="rgba(255,255,255,0.2)" sub="tickets done / day" dark />
        <MetricCard label="Carry-Forward Rate"     value={`${data.carryForwardRate}%`}    accent={data.carryForwardRate > 30 ? "#DC2626" : "rgba(255,255,255,0.2)"} sub="days with carry-forward" dark />
        <MetricCard label="Days of Data"           value={data.totalDays}                 accent="rgba(255,255,255,0.2)" sub="days in this report window" dark />
      </div>
      {data.dailyTrend && data.dailyTrend.length >= 2 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ ...lbl, marginBottom: 4 }}>14-Day Commitment Trend</div>
          <Description light style={{ marginBottom: 10 }}>Tickets committed to each morning versus how many were actually completed.</Description>
          <TrendChart data={data.dailyTrend} />
        </div>
      )}
      {data.dailyTrend && data.dailyTrend.length >= 7 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ ...lbl, marginBottom: 4 }}>Daily Completion Heatmap</div>
          <Description light style={{ marginBottom: 8 }}>One tile per day — colour shows whether that day's commitments were fully completed, partially completed, or missed.</Description>
          <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            {[["Completed", "#2563EB"], ["Partial", "#475569"], ["Missed", "#DC2626"], ["No entry", "rgba(255,255,255,0.08)"]].map(([label, color]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "rgba(255,255,255,0.4)" }}>
                <div style={{ width: 10, height: 10, borderRadius: 0, background: color, border: "1px solid rgba(255,255,255,0.1)" }} />
                {label}
              </div>
            ))}
          </div>
          <CommitmentHeatmap dailyTrend={data.dailyTrend} />
        </div>
      )}
      {blockers.length > 0 && (
        <div>
          <div style={{ ...lbl, marginBottom: 10 }}>Top Blockers</div>
          {blockers.slice(0, 6).map(([reason, count]) => <DistBar key={reason} name={reason} count={count} max={maxB} color="#3B82F6" dark />)}
        </div>
      )}
      <AIInsightHook insight={data.aiInsight} dark />
    </>
  );
}

function TeamComparisonSection({ data }) {
  if (!data || !data.developers) return <NoData />;
  const maxOpen = Math.max(...data.developers.map(d => d.open), 1);
  return (
    <>
      {data.developers.length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Open vs Critical Open</div>
          <Description style={{ marginBottom: 10, color: "#64748B" }}>
            Each developer's open ticket count next to how many of those are critical. Showing the first 8 developers.
          </Description>
          <TeamGroupedBarChart developers={data.developers} />
        </div>
      )}
      <Description style={{ marginBottom: 8, color: "#64748B" }}>Avg Age is the mean age across each developer's open and closed tickets combined.</Description>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <TableHead cols={["Developer", "Open", "Closed", "Total", "Critical Open", "Avg Age"]} />
          <tbody>
            {data.developers.map((d, i) => (
              <tr key={d.developer} style={{ background: i % 2 === 1 ? "#EEF4FF" : "#FFFFFF" }}>
                <td style={{ padding: "8px 10px", fontSize: 12, fontWeight: 600, color: "#071A2F" }}>{d.developer}</td>
                <td style={{ padding: "8px 10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#2563EB", minWidth: 24 }}>{d.open}</span>
                    <div style={{ flex: 1, minWidth: 60 }}><HBar value={d.open} max={maxOpen} color="#2563EB" /></div>
                  </div>
                </td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "#475569", fontWeight: 600 }}>{d.closed}</td>
                <td style={{ padding: "8px 10px", fontSize: 12, color: "#334155" }}>{d.total}</td>
                <td style={{ padding: "8px 10px" }}>
                  {d.criticalOpen > 0
                    ? <span style={{ background: "#FEE2E2", color: "#DC2626", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 0 }}>{d.criticalOpen}</span>
                    : <span style={{ color: "#94A3B8", fontSize: 12 }}>—</span>}
                </td>
                <td style={{ padding: "8px 10px" }}><AgeBadge days={d.avgAge} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AIInsightHook insight={data.aiInsight} />
    </>
  );
}

function OwnershipHygieneSection({ data }) {
  if (!data) return <NoData />;
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Unassigned Open ({data.unassignedCount})</div>
          <Description style={{ marginBottom: 10, color: "#64748B" }}>Open tickets with nobody assigned — nothing here can move forward until someone takes ownership.</Description>
          {data.unassigned.length === 0
            ? <p style={{ fontSize: 12, color: "#64748B" }}>All open tickets have an owner.</p>
            : data.unassigned.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <TicketLink ticketNo={t.ticketNo} />
                <span style={{ flex: 1, fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.summary}>{t.summary}</span>
                <PriorityBadge priority={t.priority} />
              </div>
            ))}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Stale — 10+ days without update ({data.staleCount})</div>
          {data.stale.length === 0
            ? <p style={{ fontSize: 12, color: "#64748B" }}>No stale tickets.</p>
            : data.stale.map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <TicketLink ticketNo={t.ticketNo} />
                <span style={{ flex: 1, fontSize: 12, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.summary}>{t.summary}</span>
                <span style={{ fontSize: 11, color: "#94A3B8" }}>{fmtDate(t.updatedOn)}</span>
              </div>
            ))}
        </div>
      </div>
      <AIInsightHook insight={data.aiInsight} />
    </>
  );
}

function RecentlyClosedSection({ data }) {
  if (!data || !data.tickets) return <NoData />;
  if (data.tickets.length === 0) return <p style={{ fontSize: 12, color: "#94A3B8" }}>No closed tickets in the selected period.</p>;
  return (
    <>
      <Description style={{ marginBottom: 8, color: "#64748B" }}>"Lifecycle" is the total number of days from ticket creation to closure.</Description>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <TableHead cols={["Ticket", "Summary", "Priority", "Assignee", "Module", "Closed On", "Lifecycle"]} />
          <tbody>
            {data.tickets.map((t, i) => {
              const lc = t.lifecycleDays;
              const lcCol = lc != null && lc > 21 ? { bg: "#FEE2E2", c: "#991B1B" } : { bg: "#F1F5F9", c: "#64748B" };
              return (
                <tr key={i} style={{ background: i % 2 === 1 ? "#EEF4FF" : "#FFFFFF" }}>
                  <td style={{ padding: "7px 10px" }}><TicketLink ticketNo={t.ticketNo} /></td>
                  <td style={{ padding: "7px 10px", fontSize: 12, color: "#334155", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.summary}>{t.summary || "—"}</td>
                  <td style={{ padding: "7px 10px" }}><PriorityBadge priority={t.priority} /></td>
                  <td style={{ padding: "7px 10px", fontSize: 12, color: "#475569" }}>{t.assignedTo || "—"}</td>
                  <td style={{ padding: "7px 10px", fontSize: 12, color: "#64748B" }}>{t.module || "—"}</td>
                  <td style={{ padding: "7px 10px", fontSize: 12, color: "#64748B" }}>{fmtDate(t.updatedOn)}</td>
                  <td style={{ padding: "7px 10px" }}>
                    <span style={{ background: lcCol.bg, color: lcCol.c, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 0 }}>
                      {lc != null ? `${lc}d` : "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AIInsightHook insight={data.aiInsight} />
    </>
  );
}

function ActionRecommendationsSection({ data }) {
  if (!data || !data.items) return <NoData />;
  const styles = {
    high:   { bg: "rgba(220,38,38,0.13)",   c: "#FCA5A5",               dot: "#FCA5A5",               label: "HIGH" },
    medium: { bg: "rgba(255,255,255,0.05)", c: "rgba(255,255,255,0.55)", dot: "rgba(255,255,255,0.3)", label: "MEDIUM" },
    low:    { bg: "rgba(255,255,255,0.03)", c: "rgba(255,255,255,0.38)", dot: "rgba(255,255,255,0.2)", label: "LOW" },
  };
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {data.items.map((item, i) => {
          const s = styles[item.severity] || styles.low;
          return (
            <div key={i} style={{ background: s.bg, borderRadius: 0, padding: "12px 16px", display: "flex", gap: 12, border: "1px solid rgba(255,255,255,0.06)" }}>
              <span style={{ fontSize: 9, color: s.dot, marginTop: 4, flexShrink: 0 }}>●</span>
              <div>
                <span style={{ fontSize: 11, fontWeight: 800, color: s.c, marginRight: 8, letterSpacing: "0.05em" }}>{s.label}</span>
                <span style={{ fontSize: 13, color: s.c }}>{item.message}</span>
              </div>
            </div>
          );
        })}
      </div>
      <AIInsightHook insight={data.aiInsight} dark />
    </>
  );
}

function TicketInventorySection({ data }) {
  if (!data || !data.tickets) return <NoData />;
  return (
    <>
      <p style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>
        Showing {data.tickets.length}{data.total > data.tickets.length ? ` of ${data.total}` : ""} ticket{data.tickets.length !== 1 ? "s" : ""}
      </p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <TableHead cols={["Ticket", "Summary", "Priority", "Status", "Assignee", "Sprint", "Module", "Age"]} />
          <tbody>
            {data.tickets.map((t, i) => (
              <tr key={t.ticketNo || i} style={{ background: i % 2 === 1 ? "#EEF4FF" : "#FFFFFF" }}>
                <td style={{ padding: "7px 10px" }}><TicketLink ticketNo={t.ticketNo} /></td>
                <td style={{ padding: "7px 10px", fontSize: 12, color: "#334155", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.summary}>{t.summary || "—"}</td>
                <td style={{ padding: "7px 10px" }}><PriorityBadge priority={t.priority} /></td>
                <td style={{ padding: "7px 10px" }}><StatusBadge status={t.status} /></td>
                <td style={{ padding: "7px 10px", fontSize: 12, color: "#475569" }}>{t.assignedTo || "—"}</td>
                <td style={{ padding: "7px 10px", fontSize: 12, color: "#64748B" }}>{t.sprint || "—"}</td>
                <td style={{ padding: "7px 10px", fontSize: 12, color: "#64748B" }}>{t.module || "—"}</td>
                <td style={{ padding: "7px 10px" }}><AgeBadge days={t.ageingDays} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AIInsightHook insight={data.aiInsight} />
    </>
  );
}

// ─── COMMAND BAR ──────────────────────────────────────────────────────────────
function CommandBar({ config, developers, sprints, isStale, loading, onPreset, onGenerate, onCustomize, onDeveloperChange, onPeriodChange, onSprintChange, onCustomDateChange }) {
  const { start: startTour } = useTour();
  const selStyle = {
    padding: "6px 10px", fontSize: 12, border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 0, background: "rgba(255,255,255,0.07)", color: "#FFFFFF", cursor: "pointer",
    outline: "none", minWidth: 140,
  };
  return (
    <div data-tour="ws-command" style={{
      background: "#0B223F", borderBottom: "1px solid rgba(255,255,255,0.08)",
      position: "sticky", top: 0, zIndex: 100,
    }} className="no-print">
      <div style={{ padding: "10px 3%", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <select value={config.developer} onChange={e => onDeveloperChange(e.target.value)} style={selStyle} className="cmd-select">
          <option value="all">All Developers</option>
          {developers.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={config.dateRange} onChange={e => onPeriodChange(e.target.value)} style={selStyle} className="cmd-select">
          <option value="7d">Last 7 Days</option>
          <option value="14d">Last 14 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="sprint">Current Sprint</option>
          <option value="custom">Custom Range</option>
          <option value="all">All Time</option>
        </select>
        {config.dateRange === "sprint" && (
          <select value={config.sprint} onChange={e => onSprintChange(e.target.value)} style={{ ...selStyle, minWidth: 160 }} className="cmd-select">
            <option value="">Select sprint…</option>
            {sprints.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {config.dateRange === "custom" && (
          <>
            <input type="date" value={config.customStart} onChange={e => onCustomDateChange("customStart", e.target.value)} style={{ ...selStyle, minWidth: 120 }} />
            <span style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>to</span>
            <input type="date" value={config.customEnd} onChange={e => onCustomDateChange("customEnd", e.target.value)} style={{ ...selStyle, minWidth: 120 }} />
          </>
        )}
        <div style={{ width: 1, height: 24, background: "rgba(255,255,255,0.1)", marginLeft: 4, marginRight: 4 }} />
        {Object.keys(PRESETS).map(name => (
          <button key={name} onClick={() => onPreset(name)}
            title={`Shows: ${[...PRESETS[name].sections].join(", ")}`}
            style={{
              padding: "5px 14px", fontSize: 11, fontWeight: 600, borderRadius: 0, cursor: "pointer",
              border: `1px solid ${config.activePreset === name ? "#3B82F6" : "rgba(255,255,255,0.12)"}`,
              background: config.activePreset === name ? "rgba(37,99,235,0.25)" : "rgba(255,255,255,0.05)",
              color: config.activePreset === name ? "#93C5FD" : "rgba(255,255,255,0.45)",
            }}>
            {name}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => startTour("workload")}
          title="Take a guided tour of the Workload report"
          style={{
            display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
            fontSize: 12, fontWeight: 600, border: "1px solid rgba(147,197,253,0.3)",
            borderRadius: 0, background: "rgba(37,99,235,0.14)", color: "#93C5FD", cursor: "pointer",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 14, height: 14, borderRadius: "50%", border: "1.5px solid currentColor",
              fontSize: 9, fontWeight: 700, lineHeight: 1,
            }}
          >
            ?
          </span>
          User Guide
        </button>
        <button onClick={onCustomize} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 14px",
          fontSize: 12, fontWeight: 600, border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 0, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.65)", cursor: "pointer",
        }}>
          <Settings size={13} />
          Customize
        </button>
        <button onClick={onGenerate} disabled={loading} style={{
          display: "flex", alignItems: "center", gap: 6, padding: "6px 16px",
          fontSize: 12, fontWeight: 700, border: "none", borderRadius: 0, cursor: loading ? "not-allowed" : "pointer",
          background: isStale ? "#2563EB" : "#3B82F6",
          color: "#FFFFFF", opacity: loading ? 0.6 : 1,
        }}>
          <RefreshCw size={13} />
          {loading ? "Generating…" : isStale ? "Refresh" : "Regenerate"}
        </button>
      </div>
    </div>
  );
}

// ─── CUSTOMIZE DRAWER ─────────────────────────────────────────────────────────
function CustomizeDrawer({ open, draft, setDraft, onApply, onClose }) {
  if (!draft) return null;
  const toggleSection = name => {
    setDraft(prev => {
      const next = new Set(prev.sections);
      if (next.has(name)) next.delete(name); else next.add(name);
      return { ...prev, sections: next };
    });
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" };
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(7,26,47,0.4)", zIndex: 199 }} />
      <div style={{
        position: "fixed", top: 0, right: 0, height: "100vh", width: 360,
        background: "#FFFFFF", borderLeft: "1px solid #BFDBFE",
        zIndex: 200, overflowY: "auto", display: "flex", flexDirection: "column",
        transform: open ? "translateX(0)" : "translateX(360px)",
        transition: "transform 0.22s ease",
        boxShadow: "-4px 0 24px rgba(7,26,47,0.12)",
      }}>
        <div style={{ padding: "16px 20px", background: "#071A2F", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#FFFFFF" }}>Customize Report</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.4)", display: "flex", alignItems: "center" }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ flex: 1, padding: "20px", overflowY: "auto" }}>
          {SECTION_GROUPS.map(group => (
            <div key={group.label} style={{ marginBottom: 20 }}>
              <div style={{ ...labelStyle, marginBottom: group.note ? 2 : 10 }}>{group.label}</div>
              {group.note && <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 8 }}>{group.note}</div>}
              {group.sections.map(name => (
                <label key={name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#334155", marginBottom: 7, cursor: "pointer" }}>
                  <input type="checkbox" checked={draft.sections.has(name)} onChange={() => toggleSection(name)} style={{ accentColor: "#2563EB" }} />
                  {name}
                </label>
              ))}
            </div>
          ))}
          <div style={{ height: 1, background: "#EEF4FF", margin: "4px 0 20px" }} />
          <div style={{ marginBottom: 20 }}>
            <div style={{ ...labelStyle, marginBottom: 10 }}>Ticket Scope</div>
            {[["all", "All Tickets"], ["open", "Open Only"], ["closed", "Closed Only"], ["critical", "Critical Only"], ["ageing", "Ageing (14+ days)"], ["idle", "Idle (5+ days)"]].map(([val, label]) => (
              <label key={val} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#334155", marginBottom: 7, cursor: "pointer" }}>
                <input type="radio" name="drawerTicketFilter" value={val}
                  checked={draft.ticketFilter === val}
                  onChange={() => setDraft(p => ({ ...p, ticketFilter: val }))}
                  style={{ accentColor: "#2563EB" }} />
                {label}
              </label>
            ))}
          </div>
          <div style={{ height: 1, background: "#EEF4FF", margin: "4px 0 20px" }} />
          <div>
            <div style={{ ...labelStyle, marginBottom: 10 }}>Default Download Format</div>
            {[["PDF", "Print to PDF"], ["Excel", "Excel (.xls)"], ["CSV", "CSV (.csv)"]].map(([val, label]) => (
              <label key={val} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#334155", marginBottom: 7, cursor: "pointer" }}>
                <input type="radio" name="drawerFormat" value={val}
                  checked={draft.format === val}
                  onChange={() => setDraft(p => ({ ...p, format: val }))}
                  style={{ accentColor: "#2563EB" }} />
                {label}
              </label>
            ))}
          </div>
        </div>
        <div style={{ padding: "14px 20px", borderTop: "1px solid #BFDBFE", display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "8px", fontSize: 12, fontWeight: 600, background: "#FFFFFF", border: "1px solid #BFDBFE", borderRadius: 0, cursor: "pointer", color: "#64748B" }}>
            Cancel
          </button>
          <button onClick={onApply} style={{ flex: 2, padding: "8px", fontSize: 12, fontWeight: 700, background: "#2563EB", border: "none", borderRadius: 0, cursor: "pointer", color: "#FFFFFF" }}>
            Apply &amp; Refresh
          </button>
        </div>
      </div>
    </>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div style={{ textAlign: "center", padding: "80px 32px", color: "#94A3B8" }}>
      <BarChart2 size={40} color="#BFDBFE" style={{ margin: "0 auto 16px" }} />
      <div style={{ fontSize: 16, fontWeight: 700, color: "#475569", marginBottom: 6 }}>Preparing your report</div>
      <div style={{ fontSize: 13 }}>Fetching data from the database…</div>
    </div>
  );
}

// ─── DOWNLOAD BAR ─────────────────────────────────────────────────────────────
function DownloadBar({ reportData, defaultFormat, onDownload }) {
  const m = reportData.meta;
  return (
    <div style={{
      background: "#071A2F", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 0,
      padding: "14px 20px", marginTop: 16,
      display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
    }} className="no-print">
      <div style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
        <span style={{ fontWeight: 600, color: "#FFFFFF" }}>{m.developer}</span>
        {" · "}{m.period}
        {" · "}<span style={{ color: "#93C5FD", fontWeight: 600 }}>{m.ticketCount} tickets</span>
        {" · "}Generated at {fmtTime(m.generatedAt)}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {["CSV", "Excel", "PDF"].map(fmt => (
          <button key={fmt} onClick={() => onDownload(fmt)} style={{
            display: "flex", alignItems: "center", gap: 5, padding: "6px 14px",
            fontSize: 12, fontWeight: 600,
            border: `1px solid ${fmt === defaultFormat ? "#3B82F6" : "rgba(255,255,255,0.15)"}`,
            borderRadius: 0,
            background: fmt === defaultFormat ? "rgba(37,99,235,0.3)" : "rgba(255,255,255,0.07)",
            color: fmt === defaultFormat ? "#93C5FD" : "rgba(255,255,255,0.5)",
            cursor: "pointer",
          }}>
            <Download size={12} />
            {fmt}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── REPORT CONTENT ───────────────────────────────────────────────────────────
function ReportContent({ reportData, config, onDownload }) {
  const { sections } = reportData;
  const sel = config.sections;
  const dev = config.developer;

  const subtitles = {
    "Commitment Analytics":  "Daily commitment reliability and trends",
    "Ageing Analysis":       "Open ticket age distribution",
    "Workload Distribution": "By priority, pipeline status, and module",
    "Team Comparison":       "Developer workload breakdown",
    "Ownership Hygiene":     "Unassigned and stale tickets",
    "Recently Closed":       "Last 20 resolved tickets",
  };

  const tier4Descs = {
    "Commitment Analytics":  "How reliably morning commitments get finished, and how often work carries over to the next day.",
    "Ageing Analysis":       "How long currently-open tickets have been sitting, oldest to newest.",
    "Workload Distribution": "How open tickets are spread across priority levels, workflow stages, and modules.",
    "Team Comparison":       "Open, closed, and critical ticket counts side by side for every developer.",
    "Ownership Hygiene":     "Tickets with no assignee, and tickets that haven't been touched in 10+ days.",
    "Recently Closed":       "The most recently resolved tickets, with total time from creation to closure.",
  };

  const tier4Components = {
    "Commitment Analytics":  <CommitmentAnalyticsSection data={sections.commitmentAnalytics} developer={dev} />,
    "Ageing Analysis":       <AgeingAnalysisSection data={sections.ageingAnalysis} />,
    "Workload Distribution": <WorkloadDistributionSection data={sections.workloadDistribution} />,
    "Team Comparison":       <TeamComparisonSection data={sections.teamComparison} />,
    "Ownership Hygiene":     <OwnershipHygieneSection data={sections.ownershipHygiene} />,
    "Recently Closed":       <RecentlyClosedSection data={sections.recentlyClosed} />,
  };

  return (
    <div>
      {/* Print header */}
      <div className="print-only" style={{ display: "none", marginBottom: 20 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#071A2F" }}>Work Intelligence Report</div>
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
          {reportData.meta.developer} · {reportData.meta.period} · {reportData.meta.ticketCount} tickets
        </div>
      </div>

      {/* Tier 0: Executive Summary (always shown) */}
      <ExecutiveSummarySection reportData={reportData} developer={dev} />

      {/* Tier 1: Workload Summary */}
      {sel.has("Workload Summary") && (
        <ReportSection id="ws-situation" title="Situation Overview" subtitle={`${reportData.meta.developer} · ${reportData.meta.period}`} icon={BarChart2}>
          <WorkloadSummarySection data={sections.workloadSummary} />
        </ReportSection>
      )}

      {/* Tier 1.5: Period Comparison + Focus Tickets */}
      {(sel.has("Period Comparison") || sel.has("Focus Tickets")) && (
        <div style={{
          display: "grid",
          gridTemplateColumns: sel.has("Period Comparison") && sel.has("Focus Tickets") ? "1fr 1fr" : "1fr",
          gap: 16, marginBottom: 16,
        }}>
          {sel.has("Period Comparison") && (
            <ReportSection id="ws-period" title="Period Comparison" subtitle="Change vs prior equivalent window" icon={RefreshCw}>
              <PeriodComparisonSection current={sections.workloadSummary} previous={sections.previousPeriod} />
            </ReportSection>
          )}
          {sel.has("Focus Tickets") && (
            <ReportSection id="ws-focus" title="Focus Tickets" subtitle="Top 5 tickets requiring attention now" desc="Same tickets and reasons as the Recommended Actions list below, filtered to the 5 most urgent." icon={Target}
              headerRight={
                <ScoreBreakdown
                  dark
                  title="How these tickets are scored"
                  intro="Each active ticket earns points from the factors below; the 5 highest scores are shown."
                  factors={[
                    { label: "Priority — Critical/Highest/High/Medium/Low/Lowest", points: "+40/+35/+25/+10/+5/+2" },
                    { label: "Age", points: "+1 per 5 days (cap +30)" },
                    { label: "Idle — no update in 5+ days", points: "+15" },
                    { label: "Status contains \"review\"", points: "+10" },
                    { label: "No assignee", points: "+8" },
                  ]}
                  footnote="riskLevel: score ≥ 50 Critical · ≥ 25 High · else Medium."
                />
              }
            >
              <FocusTicketsSection data={sections.actionRecommendations} />
            </ReportSection>
          )}
        </div>
      )}

      {/* Tier 2: Risk Center + Action Recommendations */}
      {(sel.has("Risk Center") || sel.has("Action Recommendations")) && (
        <div style={{
          display: "grid",
          gridTemplateColumns: sel.has("Risk Center") && sel.has("Action Recommendations") ? "55fr 45fr" : "1fr",
          gap: 16, marginBottom: 16,
        }}>
          {sel.has("Risk Center") && (
            <ReportSection id="ws-risk" title="Risk Center" subtitle="Critical · Ageing · Idle · Unassigned" desc="Four independent risk buckets a ticket can fall into — a ticket can appear in more than one." icon={AlertTriangle} dark>
              <RiskCenterSection riskData={sections.riskAnalysis} ownershipData={sections.ownershipHygiene} />
            </ReportSection>
          )}
          {sel.has("Action Recommendations") && (
            <ReportSection id="ws-actions" title="Recommended Actions" subtitle="Rule-based priorities" desc="Each recommendation is generated from a fixed rule against live ticket data, not AI-written — see why below each one." icon={Zap} dark>
              <ActionRecommendationsSection data={sections.actionRecommendations} />
            </ReportSection>
          )}
        </div>
      )}

      {/* Tier 3: Capacity & Delivery Outlook (full width) */}
      {sel.has("Capacity & Delivery Outlook") && (
        <ReportSection id="ws-capacity" title="Capacity & Delivery Outlook" subtitle="Backlog clearance and delivery projection" desc="Projects how long it will take to clear the open backlog at historical vs. recent pace." icon={Activity}>
          <CapacityDeliverySection capacity={sections.capacityAnalysis} forecast={sections.deliveryForecast} developer={dev} />
        </ReportSection>
      )}

      {/* Tier 4: Optional full-width sections */}
      {TIER4_ORDER.filter(name => sel.has(name)).map(name => (
        <ReportSection key={name} id={wsSlug(name)} title={name} subtitle={subtitles[name]} desc={tier4Descs[name]} icon={SECTION_ICONS[name]} dark={name === "Commitment Analytics"}>
          {tier4Components[name]}
        </ReportSection>
      ))}

      {/* Tier 5: Ticket Inventory */}
      {sel.has("Ticket Inventory") && (
        <ReportSection id="ws-inventory" title="Ticket Inventory" subtitle="Full ticket list for the selected scope" desc="Every ticket matching the current developer, date range, and status filter — the raw data behind this report." icon={List}>
          <TicketInventorySection data={sections.ticketInventory} />
        </ReportSection>
      )}

      <DownloadBar reportData={reportData} defaultFormat={config.format} onDownload={onDownload} />
    </div>
  );
}

// ─── PAGE HEADER ──────────────────────────────────────────────────────────────
function PageHeader() {
  return (
    <div style={{ background: "#071A2F", padding: "48px 3% 52px", fontFamily: FONT_FAMILY }} className="no-print">
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 28 }}>
        {["OpsIntel", "Developer Work", "Report Builder"].map((b, i, arr) => (
          <span key={b} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: i === arr.length - 1 ? "#93C5FD" : "rgba(255,255,255,0.3)", fontWeight: i === arr.length - 1 ? 600 : 400 }}>{b}</span>
            {i < arr.length - 1 && <span style={{ fontSize: 11, color: "rgba(255,255,255,0.2)" }}>/</span>}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 48, alignItems: "flex-start" }}>
        {/* Left 65% */}
        <div style={{ flex: "0 0 65%", minWidth: 0 }}>
          <div style={{ fontSize: 52, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, color: "#FFFFFF", marginBottom: 18 }}>
            Work Intelligence<br />Report Builder
          </div>
          <div style={{ fontSize: 15, color: "rgba(255,255,255,0.42)", lineHeight: 1.65, maxWidth: 520, marginBottom: 40 }}>
            Configure, generate, and export workload reports. Filter by developer, time period, and section to surface the data that matters.
          </div>
          <div style={{ display: "flex", gap: 32, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 28 }}>
            {[["12", "Report Sections"], ["3", "View Presets"], ["3", "Export Formats"]].map(([n, label]) => (
              <div key={label}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "#FFFFFF", lineHeight: 1 }}>{n}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Right — preset quick reference */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.2)", marginBottom: 10 }}>Quick Presets</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Object.entries(PRESETS).map(([name]) => (
              <div key={name} style={{
                padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 0,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>{name}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", marginTop: 2 }}>
                  {PRESETS[name].sections.size} sections · {PRESETS[name].ticketFilter} tickets
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function ReportBuilderPage() {
  const [config, setConfig] = useState({
    developer: "all",
    dateRange: "7d",
    customStart: "",
    customEnd: "",
    sprint: "",
    sections: new Set(PRESETS[DEFAULT_PRESET].sections),
    ticketFilter: PRESETS[DEFAULT_PRESET].ticketFilter,
    format: "PDF",
    activePreset: DEFAULT_PRESET,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftConfig, setDraftConfig] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [developers, setDevelopers] = useState([]);
  const [sprints, setSprints] = useState([]);

  // In-page navigator — sits under the (variable-height) sticky CommandBar.
  const cmdRef = useRef(null);
  const [cmdH, setCmdH] = useState(52);
  useEffect(() => {
    const el = cmdRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setCmdH(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const navSections = useMemo(() => {
    if (!reportData) return [];
    const sel = config.sections;
    const out = [{ id: "ws-executive", label: "Executive Summary" }];
    if (sel.has("Workload Summary")) out.push({ id: "ws-situation", label: "Situation Overview" });
    if (sel.has("Period Comparison")) out.push({ id: "ws-period", label: "Period Comparison" });
    if (sel.has("Focus Tickets")) out.push({ id: "ws-focus", label: "Focus Tickets" });
    if (sel.has("Risk Center")) out.push({ id: "ws-risk", label: "Risk Center" });
    if (sel.has("Action Recommendations")) out.push({ id: "ws-actions", label: "Recommended Actions" });
    if (sel.has("Capacity & Delivery Outlook")) out.push({ id: "ws-capacity", label: "Capacity & Delivery" });
    TIER4_ORDER.filter((n) => sel.has(n)).forEach((n) => out.push({ id: wsSlug(n), label: n }));
    if (sel.has("Ticket Inventory")) out.push({ id: "ws-inventory", label: "Ticket Inventory" });
    return out;
  }, [reportData, config.sections]);

  async function generate(cfg) {
    setLoading(true);
    setReportData(null);
    setError(null);
    setIsStale(false);
    try {
      const res = await fetch(`${API}/workload/report-data?${buildQuery(cfg)}`);
      if (!res.ok) throw new Error(`Server returned HTTP ${res.status}`);
      const data = await res.json();
      if (!data || !data.sections || data.error) {
        throw new Error(data && data.error ? data.error : "Malformed report response");
      }
      setReportData(data);
    } catch (e) {
      console.error("Report generation failed:", e);
      setError(e.message || "Could not generate the report");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    Promise.all([
      fetch(`${API}/workload/developer-list`).then(r => r.json()),
      fetch(`${API}/workload/sprints`).then(r => r.json()),
    ]).then(([devData, sprintData]) => {
      const devs = devData.developers || [];
      setDevelopers(devs);
      setSprints(sprintData.sprints || []);
      const initCfg = {
        developer: devs.length > 0 ? devs[0] : "all",
        dateRange: "7d",
        customStart: "",
        customEnd: "",
        sprint: "",
        sections: new Set(PRESETS[DEFAULT_PRESET].sections),
        ticketFilter: PRESETS[DEFAULT_PRESET].ticketFilter,
        format: "PDF",
        activePreset: DEFAULT_PRESET,
      };
      setConfig(initCfg);
      generate(initCfg);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function applyPreset(name) {
    const preset = PRESETS[name];
    const newCfg = {
      ...config,
      sections: new Set(preset.sections),
      ticketFilter: preset.ticketFilter,
      activePreset: name,
      developer: preset.forceAll ? "all" : config.developer,
    };
    setConfig(newCfg);
    generate(newCfg);
  }

  function handleDeveloperChange(val) {
    setConfig(prev => ({ ...prev, developer: val, activePreset: null }));
    setIsStale(true);
  }

  function handlePeriodChange(val) {
    setConfig(prev => ({ ...prev, dateRange: val, activePreset: null }));
    setIsStale(true);
  }

  function handleSprintChange(val) {
    setConfig(prev => ({ ...prev, sprint: val }));
    setIsStale(true);
  }

  function handleCustomDateChange(key, val) {
    setConfig(prev => ({ ...prev, [key]: val }));
    setIsStale(true);
  }

  function openDrawer() {
    setDraftConfig({ ...config, sections: new Set(config.sections) });
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setDraftConfig(null);
  }

  function applyDrawer() {
    const newCfg = { ...draftConfig, activePreset: null };
    setConfig(newCfg);
    generate(newCfg);
    closeDrawer();
  }

  function handleDownload(fmt) {
    if (!reportData) return;
    if (fmt === "CSV") downloadCSV(reportData);
    else if (fmt === "Excel") downloadExcel(reportData);
    else window.print();
  }

  return (
    <div style={{ fontFamily: FONT_FAMILY, background: "#F8FAFC", minHeight: "100vh" }}>
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 12mm; }
          html, body { background: #FFFFFF !important; }

          /* Render the designed backgrounds / badges / bars on paper */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

          .report-sidebar, .no-print { display: none !important; }
          .print-only { display: block !important; }

          /* The sidebar is hidden in print, so its fixed 290px offset must go too,
             otherwise every page is shifted right and clipped. */
          .report-main { margin-left: 0 !important; }
          .report-body { padding: 0 !important; }

          .report-section, .report-section * { box-shadow: none !important; }
          .report-section { page-break-inside: auto; margin-bottom: 12px !important; }

          /* Tables and charts fit the page; long cells wrap instead of truncating. */
          table { width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; }
          th, td { white-space: normal !important; word-break: break-word !important; overflow: visible !important; text-overflow: clip !important; }
          thead { display: table-header-group; }
          tr, img, svg { page-break-inside: avoid; }
          svg { max-width: 100% !important; height: auto !important; }
          div[style*="overflow"] { overflow: visible !important; }
          a { text-decoration: none !important; color: inherit !important; }
        }
        .print-only { display: none; }
        .cmd-select option { background: #0B223F !important; color: #FFFFFF !important; }
        [id^="ws-"] { scroll-margin-top: 132px; }
      `}</style>

      <div className="no-print">
        <Sidebar />
      </div>

      <div className="report-main" style={{ marginLeft: SIDEBAR_WIDTH }}>
        <PageHeader />

        <div ref={cmdRef}>
          <CommandBar
            config={config}
            developers={developers}
            sprints={sprints}
            isStale={isStale}
            loading={loading}
            onPreset={applyPreset}
            onGenerate={() => generate(config)}
            onCustomize={openDrawer}
            onDeveloperChange={handleDeveloperChange}
            onPeriodChange={handlePeriodChange}
            onSprintChange={handleSprintChange}
            onCustomDateChange={handleCustomDateChange}
          />
        </div>

        {reportData && navSections.length > 1 && (
          <SectionNav sections={navSections} topOffset={cmdH} />
        )}

        <CustomizeDrawer
          open={drawerOpen}
          draft={draftConfig}
          setDraft={setDraftConfig}
          onApply={applyDrawer}
          onClose={closeDrawer}
        />

        <div className="report-body" style={{ width: "100%", boxSizing: "border-box", padding: "24px 3%" }}>
          {!reportData && !loading && !error && <EmptyState />}
          {!loading && error && (
            <div style={{ padding: "48px 24px", textAlign: "center", background: "#FFFFFF", border: "1px solid #FECACA", borderRadius: 0, maxWidth: 520, margin: "0 auto" }}>
              <AlertTriangle size={24} color="#DC2626" style={{ margin: "0 auto 12px", display: "block" }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: "#991B1B", marginBottom: 6 }}>Report generation failed</div>
              <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>{error}</div>
              <button
                onClick={() => generate(config)}
                style={{ padding: "8px 18px", background: "#2563EB", color: "#FFFFFF", border: "none", borderRadius: 0, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT_FAMILY }}
              >
                Retry
              </button>
            </div>
          )}
          {loading && (
            <div style={{ padding: 60, textAlign: "center", color: "#64748B", fontSize: 13 }}>
              <RefreshCw size={24} color="#BFDBFE" style={{ margin: "0 auto 12px", display: "block" }} />
              Generating report…
            </div>
          )}
          {reportData && (
            <ReportContent
              reportData={reportData}
              config={config}
              onDownload={handleDownload}
            />
          )}
        </div>
      </div>
    </div>
  );
}
